import { OPPORTUNITIES_STACK_BY_TYPE_ENUM, OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM } from '../constants';

interface Opportunity {
  id: string;
  sellerCompany?: { name?: string };
  sellerStake?: number;
  sellerMotivation?: string;
  npv?: number;
  asset?: { displayName?: string; name?: string };
  country?: { displayName?: string };
  group?: string;
}

// Helper function to format hover information for individual opportunities
export const formatOpportunityHoverInfo = (opportunity: Opportunity): string => {
  const sellerName = opportunity.sellerCompany?.name || 'N/A';
  const sellerStake = opportunity.sellerStake ? `${(opportunity.sellerStake * 100).toFixed(1)}%` : 'N/A';
  const motivation = opportunity.sellerMotivation || 'N/A';
  const npv = opportunity.npv || 0;
  const npvDisplay = npv > 0 ? `${npv.toFixed(1)}` : 'N/A';

  return `${sellerName} (${sellerStake})<br>${npvDisplay}<br>${motivation}`;
};

// Helper function to find opportunities for a specific group and stack
export const findOpportunitiesForGroup = (
  opportunities: Opportunity[] | null | undefined,
  group: string | null | undefined,
  stackBy: string | null | undefined,
  stackValue: string | null | undefined
): Opportunity[] => {
  if (!opportunities || opportunities.length === 0) {
    return [];
  }
  
  return opportunities.filter(opp => {
    let matchesGroup: boolean;
    let matchesStack: boolean;
    
    if (stackBy === OPPORTUNITIES_STACK_BY_TYPE_ENUM.ASSET) {
      // When stacking by asset:
      // - group is the country name (e.g., "Peru")
      // - stackValue is the asset name (e.g., "Toldado")
      matchesGroup = opp.country?.displayName === group;
      matchesStack = opp.asset?.displayName === stackValue;
    } else if (stackBy === OPPORTUNITIES_STACK_BY_TYPE_ENUM.SELLER) {
      // When stacking by seller:
      // - group is the country name (e.g., "Peru")
      // - stackValue is the seller company name
      matchesGroup = opp.country?.displayName === group;
      matchesStack = opp.sellerCompany?.name === stackValue;
    } else if (stackBy === null || stackBy === undefined) {
      // For flat charts (no stacking):
      // - group is the country name (e.g., "Peru")
      // - just match by country
      matchesGroup = opp.country?.displayName === group;
      matchesStack = true;
    } else {
      // Default case - when not stacking or using other aggregation types
      matchesGroup = opp.group === group;
      matchesStack = true;
    }
    
    return matchesGroup && matchesStack;
  });
};

// Helper function to create hover text for a group of opportunities
export const createHoverText = (
  opportunities: Opportunity[] | null | undefined,
  aggregateByTitle: string,
  stackByTitle: string | null = null
): string => {
  if (!opportunities || opportunities.length === 0) {
    return '';
  }

  // Get first opportunity for common data
  const firstOpp = opportunities[0];
  if (!firstOpp) return '';

  const sellerName = firstOpp.sellerCompany?.name || 'N/A';
  const sellerStake = firstOpp.sellerStake ? `${(firstOpp.sellerStake * 100).toFixed(1)}%` : 'N/A';
  const motivation = firstOpp.sellerMotivation || 'N/A';
  const npv = firstOpp.npv || 0;
  const npvDisplay = npv > 0 ? `${npv.toFixed(1)}` : 'N/A';

  // Determine the missing dimension (neither aggregated by nor stacked by)
  let missingDimension = '';
  
  // Check the actual values, not the titles
  if (
    aggregateByTitle.toLowerCase() === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COUNTRY &&
    stackByTitle?.toLowerCase() === OPPORTUNITIES_STACK_BY_TYPE_ENUM.SELLER
  ) {
    // Country-Seller: show Asset
    const assetName = firstOpp.asset?.displayName || firstOpp.asset?.name || 'N/A';
    missingDimension = `Asset: ${assetName}`;
  } else if (
    aggregateByTitle.toLowerCase() === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COUNTRY &&
    stackByTitle?.toLowerCase() === OPPORTUNITIES_STACK_BY_TYPE_ENUM.ASSET
  ) {
    // Country-Asset: show Seller
    missingDimension = `Seller: ${sellerName}`;
  } else if (
    aggregateByTitle.toLowerCase() === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COMPANY &&
    stackByTitle?.toLowerCase() === OPPORTUNITIES_STACK_BY_TYPE_ENUM.ASSET
  ) {
    // Seller-Asset: show Country
    const countryName = firstOpp.country?.displayName || 'N/A';
    missingDimension = `Country: ${countryName}`;
  }

  // If only one opportunity, return simple format
  if (opportunities.length === 1) {
    return `<br>${missingDimension}<br>${sellerName} (${sellerStake}) - ${motivation}`;
  }

  // Multiple opportunities - show detailed breakdown
  let hoverText = '';

  // Show first opportunity
  hoverText += `<br>${missingDimension}<br>${sellerName} (${sellerStake}) - ${motivation}<br>Opportunity NPV10 (US$MM): ${npvDisplay}`;

  // Show second opportunity
  if (opportunities.length > 1) {
    const secondOpp = opportunities[1];
    if (!secondOpp) return hoverText;

    const secondSellerName = secondOpp.sellerCompany?.name || 'N/A';
    const secondSellerStake = secondOpp.sellerStake
      ? `${(secondOpp.sellerStake * 100).toFixed(1)}%`
      : 'N/A';
    const secondMotivation = secondOpp.sellerMotivation || 'N/A';
    const secondNpv = secondOpp.npv || 0;
    const secondNpvDisplay = secondNpv > 0 ? `${secondNpv.toFixed(1)}` : 'N/A';

    // Show missing dimension for second opportunity
    let secondMissingDimension = '';
    if (
      aggregateByTitle.toLowerCase() === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COUNTRY &&
      stackByTitle?.toLowerCase() === OPPORTUNITIES_STACK_BY_TYPE_ENUM.SELLER
    ) {
      // Country-Seller: show Asset
      const secondAssetName = secondOpp.asset?.displayName || secondOpp.asset?.name || 'N/A';
      secondMissingDimension = `Asset: ${secondAssetName}`;
    } else if (
      aggregateByTitle.toLowerCase() === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COUNTRY &&
      stackByTitle?.toLowerCase() === OPPORTUNITIES_STACK_BY_TYPE_ENUM.ASSET
    ) {
      // Country-Asset: show Seller
      secondMissingDimension = `Seller: ${secondSellerName}`;
    } else if (
      aggregateByTitle.toLowerCase() === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COMPANY &&
      stackByTitle?.toLowerCase() === OPPORTUNITIES_STACK_BY_TYPE_ENUM.ASSET
    ) {
      // Seller-Asset: show Country
      const secondCountryName = secondOpp.country?.displayName || 'N/A';
      secondMissingDimension = `Country: ${secondCountryName}`;
    }
    hoverText += `<br><br>${secondMissingDimension}<br>${secondSellerName} (${secondSellerStake}) - ${secondMotivation}<br>Opportunity NPV10 (US$MM): ${secondNpvDisplay}`;
  }

  return hoverText;
};
